import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import styles from './FaultyItemsPage.module.css'; // We'll create/update this file
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { RiEdit2Line, RiToolsLine } from "react-icons/ri"; // Keep other icons
import SerialNumberBadge from '../../components/product/SerialNumberBadge'; // Import the SerialNumberBadge

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const filterFaultyItems = (items, filters, searchTerm) => {
  let result = items;

  if (filters.status && filters.status !== 'all') {
    result = result.filter(item => item.status === filters.status);
  }

  if (filters.severity && filters.severity !== 'all') {
    result = result.filter(item => item.severity === filters.severity);
  }

  if (searchTerm) {
    const searchTermLower = searchTerm.toLowerCase();
    result = result.filter(item =>
      item.productName?.toLowerCase().includes(searchTermLower) ||
      item.productCode?.toLowerCase().includes(searchTermLower) ||
      item.faultDescription?.toLowerCase().includes(searchTermLower)
    );
  }

  return result;
};

function FaultyItemsPage() {
  const [faultyItems, setFaultyItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    status: 'all', // Default to 'all' or a specific status like 'Faulty'
    severity: 'all',
  });
  const [activeTooltip, setActiveTooltip] = useState({
    item: null, // Store the full item data for the tooltip content
    rect: null,   // Store BoundingClientRect of the trigger
  });

  const navigate = useNavigate(); // Initialize useNavigate

  const fetchFaultyItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/faulty-items`);
      setFaultyItems(data);
    } catch (error) {
      console.error('Failed to fetch faulty items:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch faulty items.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFaultyItems();
  }, [fetchFaultyItems]);

  const filteredItems = React.useMemo(() =>
    filterFaultyItems(faultyItems, filters, debouncedSearch),
    [faultyItems, filters, debouncedSearch]
  );

  const debouncedSetSearch = React.useMemo(
    () => debounce((value) => setDebouncedSearch(value), 300),
    [setDebouncedSearch]
  );

  const handleMarkAsRepaired = async (itemId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/faulty-items/${itemId}/repair`);
      toast.success(`Item marked as repaired and returned to inventory.`);
      fetchFaultyItems();
    } catch (error) {
      console.error('Failed to mark item as repaired:', error);
      toast.error(error.response?.data?.message || 'Failed to mark item as repaired.');
    }
  };

  // Pass the entire item object in the state
  const handleEditItem = (item) => {
    navigate(`/EditFaultyItem`, { state: { faultyItemData: item } });
  };

  const handleMouseEnterTooltip = (item, event) => {
    const targetRect = event.currentTarget.getBoundingClientRect();
    setActiveTooltip({
      item: item,
      rect: targetRect,
    });
  };

  const handleMouseLeaveTooltip = () => {
    setActiveTooltip({
      item: null,
      rect: null,
    });
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.pageTitle}>Faulty Items Management</h2>

      {isLoading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading faulty items...</p>
        </div>
      ) : (
        <>
          <div className={styles.filterSection}>
            <input
              type="text"
              placeholder="Search by Product Name, Code, or Description..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                debouncedSetSearch(e.target.value);
              }}
              className={styles.searchInput}
            />
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className={styles.filterSelect}
            >
              <option value="all">All Statuses</option>
              <option value="Faulty">Faulty</option>
              <option value="Under Repair">Under Repair</option>
              <option value="Repaired">Repaired</option>
              <option value="Unrepairable">Unrepairable</option>
            </select>
            <select
              value={filters.severity}
              onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
              className={styles.filterSelect}
            >
              <option value="all">All Severities</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Critical">Critical</option>
            </select>
          </div>

          <div className={styles.itemsTable}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.tableHeader}>Product Name</th>
                  <th className={styles.tableHeader}>Product Code</th>
                  <th className={styles.tableHeader}>Fault Description</th>
                  <th className={styles.tableHeader}>Severity</th>
                  <th className={styles.tableHeader}>Managed By Serial</th>
                  <th className={styles.tableHeader}>Quantity</th>
                  <th className={styles.tableHeader}>Status</th>
                  <th className={styles.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item._id} className={styles.tableRow}>
                    <td className={styles.tableCell}>{item.productName}</td>
                    <td className={styles.tableCell}> {/* Product Code cell */}
                      <div
                        className={styles.serialTooltipContainer}
                        onMouseEnter={(e) => item.managedBySerial && item.faultySerialNumbers?.length > 0 && handleMouseEnterTooltip(item, e)}
                        onMouseLeave={handleMouseLeaveTooltip}
                      >
                        {item.productCode}
                      </div>
                    </td>
                    <td className={styles.tableCell}>{item.faultDescription}</td>
                    <td className={styles.tableCell}>{item.severity}</td>
                    <td className={styles.tableCell}>{item.managedBySerial ? 'Yes' : 'No'}</td>
                    <td className={styles.tableCell}>{item.managedBySerial ? item.faultySerialNumbers.length : item.faultyQuantity}</td>
                    <td className={styles.tableCell}>{item.status}</td>
                    <td className={`${styles.tableCell} ${styles.actionsCell}`}>
                      {item.status !== 'Repaired' && (
                        <button
                          onClick={() => handleMarkAsRepaired(item._id)}
                          className={`${styles.actionButton} ${styles.repairButton}`}
                          title="Mark as Repaired (all)" 
                        ><RiToolsLine /></button>
                      )}
                      <button
                        onClick={() => handleEditItem(item)} 
                        className={`${styles.actionButton} ${styles.editButton}`} 
                        title="Edit Item (partial returns)"
                      ><RiEdit2Line /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {activeTooltip.item && activeTooltip.rect && ReactDOM.createPortal(
        <div
          className={styles.serialTooltip}
          style={{
            // Position the tooltip above the trigger element
            top: `${activeTooltip.rect.top}px`,
            left: `${activeTooltip.rect.left + activeTooltip.rect.width / 2}px`,
            transform: 'translate(-50%, -100%) translateY(-10px)', // Center horizontally, move above, add 10px gap
            opacity: 1,
            visibility: 'visible',
          }}
        >
          <strong>Faulty Serial Numbers:</strong>
          <div className={styles.serialTagsContainer}>
            {activeTooltip.item.faultySerialNumbers.map((sn) => (
              <SerialNumberBadge
                key={sn}
                serialNumber={sn}
                isRemovable={false}
              />
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default FaultyItemsPage;
